import { Discount, Price, PriceInterval } from '@griply/types';
import { Skeleton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { differenceInDays } from 'date-fns';
import React, { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Info16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { currencySignMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { ID } from 'shared/types/id';

import * as Styled from './payment-options.style';

type LoaderOption = {
  id: ID;
  translationLabel: string;
  selected?: boolean;
};

const loaderOptions: LoaderOption[] = [
  {
    id: 'year',
    translationLabel: 'dialogs.premium.payment-options.period.year',
    selected: true,
  },
  {
    id: 'month',
    translationLabel: 'dialogs.premium.payment-options.period.month',
  },
];

export type LoadingPaymentOptionsProps = {
  options?: LoaderOption[];
};

export const LoadingPaymentOptions: React.FC<LoadingPaymentOptionsProps> = ({
  options = loaderOptions,
}) => {
  const { t } = useTranslation();

  return (
    <Styled.List>
      {options.map(({ id, translationLabel, selected }) => (
        <Styled.ListItem key={id}>
          <Styled.OptionButton $selected={!!selected} disabled>
            <Styled.OptionHeader>
              <Typography variant="h6" component="span">
                {t(translationLabel)}
              </Typography>
            </Styled.OptionHeader>

            <Styled.OptionBody>
              <Typography variant="subtitle2" component="span">
                <Skeleton animation="wave" width="10.4rem" />
              </Typography>
            </Styled.OptionBody>
          </Styled.OptionButton>
        </Styled.ListItem>
      ))}
    </Styled.List>
  );
};

export type PaymentOptionsProps = {
  options: Price[];
  discount?: Discount;
  onOption: (id: ID) => void;
  selectedOption: string;
  disabled?: boolean;
  // the lifetime-deal card sells the deal, not the annual saving — it turns the badge off
  showSavingPercentage?: boolean;
};

export const PaymentOptions: React.FC<PaymentOptionsProps> = ({
  options,
  discount,
  onOption,
  selectedOption,
  disabled,
  showSavingPercentage = true,
}) => {
  const lifetimeUsp2Ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const today = useToday();

  return (
    <Styled.List>
      {options.map(
        ({
          id,
          interval,
          amountInCents,
          currency,
          savingPercentage,
          expires,
          coupon,
        }) => {
          const isSelected = id === selectedOption;
          const onClick = () => onOption(id);

          const optionDiscount = discount?.applicableIntervals.includes(
            interval,
          )
            ? discount
            : undefined;

          // A campaign's end date reaches us on the coupon; a lifetime price can also carry its
          // own. Either one counts down, and a coupon must never suppress it.
          const countdownDate = coupon?.expiresAt ?? expires;

          // take the discount percentage of the price
          const discountedPrice = coupon
            ? amountInCents - coupon.saving
            : amountInCents * ((100 - (optionDiscount?.percentage ?? 0)) / 100);

          const centsPerMonth = [
            PriceInterval.Month,
            PriceInterval.Lifetime,
          ].includes(interval)
            ? discountedPrice
            : discountedPrice / 12;

          const currencySign = currencySignMap[currency] ?? currency;

          return (
            <Styled.ListItem key={id}>
              <Styled.OptionButton
                $selected={isSelected}
                onClick={onClick}
                disabled={disabled}
                type="button"
              >
                <Styled.OptionHeader>
                  <Styled.TitleContainer>
                    <Typography variant="h6" component="span">
                      {t(`dialogs.premium.payment-options.period.${interval}`)}
                    </Typography>

                    {!!coupon && (
                      <Styled.Discount>
                        {coupon.percentage}% off
                      </Styled.Discount>
                    )}

                    {!!countdownDate && (
                      <Styled.Discount>
                        {t('dialogs.premium.payment-options.expires', {
                          count: differenceInDays(
                            new Date(countdownDate),
                            today,
                          ),
                        })}
                      </Styled.Discount>
                    )}

                    {!coupon &&
                      (!!optionDiscount?.percentage ||
                        (showSavingPercentage && !!savingPercentage)) && (
                        <Styled.Discount>
                          {t(
                            optionDiscount?.percentage
                              ? 'dialogs.premium.payment-options.discount.percentage'
                              : 'dialogs.premium.payment-options.saving.percentage',
                            {
                              percentage: Math.round(
                                optionDiscount?.percentage ?? savingPercentage,
                              ),
                            },
                          )}
                        </Styled.Discount>
                      )}
                  </Styled.TitleContainer>

                  {interval === PriceInterval.Year && (
                    <Styled.OptionMetaContainer>
                      {discountedPrice !== amountInCents && (
                        <Styled.OldPrice>
                          {currencySign}
                          {(amountInCents / 100).toFixed(2)}
                        </Styled.OldPrice>
                      )}
                      <Styled.Price>
                        {currencySign}
                        {(discountedPrice / 100).toFixed(2)}
                      </Styled.Price>
                    </Styled.OptionMetaContainer>
                  )}
                  {interval === PriceInterval.Lifetime && (
                    <>
                      <Styled.OptionMetaContainer>
                        <Styled.OptionMetaList>
                          <Styled.OptionMetaItem>
                            <Typography variant="buttonSmall">
                              {t(
                                'dialogs.premium.payment-options.lifetime.usp.1',
                              )}
                            </Typography>
                          </Styled.OptionMetaItem>
                          <Styled.OptionMetaItem>
                            <Typography variant="buttonSmall">
                              {t(
                                'dialogs.premium.payment-options.lifetime.usp.2',
                              )}
                              <Styled.UspTooltipIconContainer
                                ref={lifetimeUsp2Ref}
                              >
                                <Icon icon={Info16} />
                              </Styled.UspTooltipIconContainer>
                              <Tooltip
                                label={t(
                                  'dialogs.premium.payment-options.lifetime.usp.2.description',
                                )}
                                referenceElement={lifetimeUsp2Ref}
                              />
                            </Typography>
                          </Styled.OptionMetaItem>
                        </Styled.OptionMetaList>
                      </Styled.OptionMetaContainer>
                    </>
                  )}
                </Styled.OptionHeader>

                <Styled.OptionBody>
                  <Typography variant="subtitle2" component="span">
                    <Trans
                      i18nKey={`dialogs.premium.payment-options.interval.${interval}`}
                      values={{
                        amount: (Math.round(centsPerMonth) / 100).toFixed(2),
                        currency: currencySign,
                      }}
                      components={{ period: <Styled.Period /> }}
                    />
                  </Typography>
                  {!!coupon && (
                    <Styled.CouponPrice>
                      {currencySign}
                      {(amountInCents / 100).toFixed(2)}
                    </Styled.CouponPrice>
                  )}
                </Styled.OptionBody>
              </Styled.OptionButton>
            </Styled.ListItem>
          );
        },
      )}
    </Styled.List>
  );
};
